import { getAuthHeader, getAuthToken, API_BASE_URL } from './base';

export const uploadMediaFile = async (file) => {
  try {
    const headers = await getAuthHeader();
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${API_BASE_URL}/api/media/library/upload`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to upload media");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading media:", error);
    throw error;
  }
};

export const getMediaFiles = async () => {
  try {
    const headers = await getAuthHeader();

    const response = await fetch(`${API_BASE_URL}/api/media/library`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch media files");
    }

    const data = await response.json();
    const files = data.files || [];

    // Auto-catalog: if no image files found, trigger one-time migration
    // of existing Firebase Storage files into org-scoped MongoDB records
    if (files.filter(f => f.type === "image").length === 0) {
      try {
        const migHeaders = await getAuthHeader();
        const migRes = await fetch(`${API_BASE_URL}/api/media/library/migrate`, {
          method: "POST",
          headers: migHeaders,
        });
        if (migRes.ok) {
          const migData = await migRes.json();
          if (migData.created > 0) {
            const retry = await fetch(`${API_BASE_URL}/api/media/library`, {
              method: "GET",
              headers: await getAuthHeader(),
            });
            if (retry.ok) {
              const retryData = await retry.json();
              return retryData.files || [];
            }
          }
        }
      } catch (_) {}
    }

    return files;
  } catch (error) {
    console.error("Error fetching media files:", error);
    return [];
  }
};

export const deleteMediaFile = async (fileId) => {
  try {
    const headers = await getAuthHeader();

    const response = await fetch(`${API_BASE_URL}/api/media/library/${encodeURIComponent(fileId)}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to delete media file");
    }

    return { success: true };
  } catch (error) {
    console.error("Error deleting media file:", error);
    throw error;
  }
};

export const saveVideoUrl = async (videoUrl, title = "") => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/media/library/video`, {
      method: "POST",
      headers,
      body: JSON.stringify({ videoUrl, title }),
    });

    if (!response.ok) {
      throw new Error("Failed to save video URL");
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving video URL:", error);
    throw error;
  }
};

export const getVideoUrls = async () => {
  try {
    const headers = await getAuthHeader();

    const response = await fetch(`${API_BASE_URL}/api/media/library/videos`, {
      method: "GET",
      headers,
    });

    if (!response.ok) {
      throw new Error("Failed to fetch video URLs");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching video URLs:", error);
    return [];
  }
};

export const deleteVideoUrl = async (videoUrl) => {
  try {
    const headers = await getAuthHeader();
    headers["Content-Type"] = "application/json";

    const response = await fetch(`${API_BASE_URL}/api/media/library/video`, {
      method: "DELETE",
      headers,
      body: JSON.stringify({ videoUrl }),
    });

    if (!response.ok) {
      throw new Error("Failed to delete video URL");
    }

    return true;
  } catch (error) {
    console.error("Error deleting video URL:", error);
    throw error;
  }
};

export const uploadTemporaryImages = async (formData) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api/media/upload-temporary`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Upload failed");
    }

    return await response.json();
  } catch (error) {
    console.error("Error uploading temporary images:", error);
    throw error;
  }
};

export const deleteTemporaryImages = async (imageIds) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api/media/delete-temporary`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ imageIds }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Delete failed");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting temporary images:", error);
    throw error;
  }
};

export const markImagesAsUsed = async (imageIds, surveyId) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/api/media/mark-used`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ imageIds, surveyId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to mark images");
    }

    return await response.json();
  } catch (error) {
    console.error("Error marking images as used:", error);
    throw error;
  }
};
